<template>
  <div>
    <!-- ── Barra de acciones ──────────────────────────────────────── -->
    <div class="row g-2 align-items-center mb-3 mt-2">
      <div class="col-auto" v-if="mostrarBotonTodos">
        <button
          class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          @click="$emit('refresh')"
          title="Recargar"
        >
          <span class="material-icons" style="font-size:16px">refresh</span> Todos
        </button>
      </div>
      <div class="col-auto" v-if="mostrarBotonNuevo">
        <button
          class="btn btn-primary btn-sm d-flex align-items-center gap-1"
          @click="$emit('create')"
          title="Nuevo registro"
        >
          <span class="material-icons" style="font-size:16px">add</span> Nuevo
        </button>
      </div>
      <!-- Slot para botones extra (exportar, etc.) -->
      <slot name="acciones-extra" />
      <div class="col-auto ms-auto d-flex align-items-center gap-2">
        <label class="small fw-bold mb-0">Mostrar</label>
        <select
          v-model.number="elementosPorPagina"
          class="form-select form-select-sm"
          style="width:75px"
          @change="aplicarPaginacion(1)"
        >
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="small text-muted">registros</span>
      </div>
    </div>

    <!-- ── Tabla ──────────────────────────────────────────────────── -->
    <div class="table-responsive" v-if="data.length > 0">
      <table class="table table-hover table-bordered table-sm">
        <thead class="table-dark">
          <!-- Encabezados ordenables -->
          <tr>
            <th
              v-for="col in columns"
              :key="col.field"
              class="th-sort"
              @click="ordenarPor(col.field)"
            >
              {{ col.label }}
              <span>{{ iconOrden(col.field) }}</span>
            </th>
            <th v-if="acciones" style="width:90px">Opciones</th>
          </tr>
          <!-- Filtros por columna -->
          <tr>
            <th v-for="col in columns" :key="col.field">
              <input
                v-model="filtros[col.field]"
                @input="aplicarPaginacion(1)"
                class="form-control form-control-sm"
                placeholder="Filtrar..."
              />
            </th>
            <th v-if="acciones"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in datosPaginados"
            :key="row[idField]"
            @dblclick="$emit('edit', row)"
          >
            <td v-for="col in columns" :key="col.field">
              <!-- Slot por columna para render personalizado -->
              <slot :name="`cell-${col.field}`" :row="row">
                {{ col.format ? col.format(row[col.field], row) : row[col.field] }}
              </slot>
            </td>
            <td v-if="acciones">
              <div class="d-flex gap-1">
                <button
                  v-if="mostrarEditar"
                  class="btn btn-sm btn-outline-warning"
                  @click="$emit('edit', row)"
                  title="Editar"
                >
                  <span class="material-icons" style="font-size:14px">edit</span>
                </button>
                <button
                  v-if="mostrarVer"
                  class="btn btn-sm btn-outline-primary"
                  @click="$emit('view', row)"
                  title="Ver detalle"
                >
                  <span class="material-icons" style="font-size:14px">open_in_new</span>
                </button>
                <!-- Slot para acciones extra por fila -->
                <slot name="acciones-fila" :row="row" />
                <button
                  v-if="mostrarEliminar"
                  class="btn btn-sm btn-outline-danger"
                  @click="$emit('delete', row)"
                  title="Eliminar"
                >
                  <span class="material-icons" style="font-size:14px">delete</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-secondary">
            <td :colspan="acciones ? columns.length + 1 : columns.length" class="text-end fw-bold small">
              Mostrando {{ datosPaginados.length }} de {{ dataOrdenada.length }} registros
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div v-else class="alert alert-info text-center mt-3">
      {{ mensajeVacio }}
    </div>

    <!-- ── Paginación ─────────────────────────────────────────────── -->
    <div
      class="d-flex justify-content-between align-items-center mt-3"
      v-if="data.length > 0"
    >
      <span class="small text-muted">
        Página {{ paginaActual }} de {{ totalPaginas }} — {{ dataOrdenada.length }} registros
      </span>
      <nav aria-label="Paginación">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(1)">«</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(paginaActual - 1)">‹</a>
          </li>
          <li
            v-for="p in paginasVisibles"
            :key="p"
            class="page-item"
            :class="{ active: p === paginaActual, disabled: p === '...' }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="p !== '...' && aplicarPaginacion(p)"
            >{{ p }}</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(paginaActual + 1)">›</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(totalPaginas)">»</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  name: "DataTable",
  emits: ["refresh", "create", "edit", "view", "delete"],
  props: {
    data:              { type: Array,   default: () => [] },
    columns:           { type: Array,   required: true },
    idField:           { type: String,  default: "id" },
    acciones:          { type: Boolean, default: true },
    mostrarBotonTodos: { type: Boolean, default: true },
    mostrarBotonNuevo: { type: Boolean, default: true },
    mostrarEditar:     { type: Boolean, default: true },
    mostrarVer:        { type: Boolean, default: true },
    mostrarEliminar:   { type: Boolean, default: true },
    mensajeVacio:      { type: String,  default: "No hay registros disponibles." },
  },
  data() {
    return {
      filtros:            {},
      campoOrden:         "",
      ascendente:         true,
      paginaActual:       1,
      elementosPorPagina: 10,
      datosPaginados:     [],
    };
  },
  computed: {
  dataFiltrada() {
    return this.data.filter(row =>
      Object.keys(this.filtros).every(f => {
        const val = this.filtros[f];
        if (!val) return true;
        return String(row[f] ?? "").toLowerCase().includes(val.toLowerCase());
      })
    );
  },
  dataOrdenada() {
    const filtrada = this.data.filter(row =>
      Object.keys(this.filtros).every(f => {
        const val = this.filtros[f];
        if (val === '' || val === null || val === undefined) return true;
        const col = this.columns.find(c => c.field === f);
        if (col?.filterType === 'select') {
          return String(row[f] ?? '') === String(val);
        }
        return String(row[f] ?? '').toLowerCase().includes(val.toLowerCase());
      })
    );
    if (!this.campoOrden) return filtrada;
    return [...filtrada].sort((a, b) => {
      const va = a[this.campoOrden] ?? '';
      const vb = b[this.campoOrden] ?? '';

      const col = this.columns.find(c => c.field === this.campoOrden);

      // 1. Fecha
      const esISO = v => /^\d{4}-\d{2}-\d{2}/.test(String(v));
      const esDMY = v => /^\d{2}[/-]\d{2}[/-]\d{4}/.test(String(v));
      const parseDMY = v => {
        const [d, m, y] = String(v).split(/[/-]/);
        return new Date(`${y}-${m}-${d}`);
      };
      const ambosF = col?.date || (esISO(va) && esISO(vb)) || (esDMY(va) && esDMY(vb));
      if (ambosF) {
        const da = esDMY(va) ? parseDMY(va) : new Date(va);
        const db = esDMY(vb) ? parseDMY(vb) : new Date(vb);
        return this.ascendente ? da - db : db - da;
      }

      // 2. Numérico
      const na = Number(va);
      const nb = Number(vb);
      const ambosN = col?.numeric ||
        (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb));
      if (ambosN) {
        return this.ascendente ? na - nb : nb - na;
      }

      // 3. Texto
      const cmp = String(va).localeCompare(String(vb), 'es');
      return this.ascendente ? cmp : -cmp;
    });
  },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.dataOrdenada.length / this.elementosPorPagina));
    },
    paginasVisibles() {
      const total  = this.totalPaginas;
      const actual = this.paginaActual;
      const delta  = 2;
      const rango  = [];
      for (let i = Math.max(2, actual - delta); i <= Math.min(total - 1, actual + delta); i++) {
        rango.push(i);
      }
      if (rango.length && rango[0] > 2)                        rango.unshift("...");
      if (rango.length && rango[rango.length - 1] < total - 1) rango.push("...");
      const paginas = [1];
      rango.forEach(p => paginas.push(p));
      if (total > 1) paginas.push(total);
      return paginas;
    },
  },
  watch: {
    data: {
      immediate: true,
      handler() { this.aplicarPaginacion(1); },
    },
  },
  methods: {
    ordenarPor(campo) {
      if (this.campoOrden === campo) {
        this.ascendente = !this.ascendente;
      } else {
        this.campoOrden = campo;
        this.ascendente = true;
      }
      this.aplicarPaginacion(this.paginaActual);
    },
    iconOrden(campo) {
      if (this.campoOrden !== campo) return "⇅";
      return this.ascendente ? "↑" : "↓";
    },
    aplicarPaginacion(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas) return;
      this.paginaActual   = pagina;
      const ini           = (pagina - 1) * this.elementosPorPagina;
      this.datosPaginados = this.dataOrdenada.slice(ini, ini + this.elementosPorPagina);
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover { background-color: #3d5166; }
.table-hover tbody tr { cursor: pointer; }
</style>